import {
  Close as DialogClose,
  Content as DialogContent,
  Overlay as DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
  Title as DialogTitle,
} from "@radix-ui/react-dialog";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

const FOCUS_DELAY = 100;
const MAX_OTP_LENGTH = 6;
const ICON_SIZE = 20;

import { MdArrowBack } from "react-icons/md";
import { Button } from "../../../components/button";
import styles from "./index.module.css";

type TwoFactorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (otpCode: string) => Promise<void>;
  isLoading: boolean;
  errorMessage?: string;
};

export const TwoFactorDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  errorMessage,
}: TwoFactorDialogProps) => {
  const [otpCode, setOtpCode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOtpCode("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, FOCUS_DELAY);
    }
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (otpCode.trim()) {
      try {
        await onSubmit(otpCode.trim());
      } catch {
        // Error is handled by parent component
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <DialogRoot onOpenChange={onOpenChange} open={open}>
      <DialogPortal>
        <DialogOverlay className={styles.dialogOverlay} />
        <DialogContent className={styles.dialogContent}>
          <div className={styles.dialogHeader}>
            <DialogClose
              aria-label="Close dialog"
              className={styles.dialogClose}
              disabled={isLoading}
              onClick={handleClose}
            >
              <MdArrowBack color="#23005C" size={ICON_SIZE} />
            </DialogClose>
            <DialogTitle className={styles.dialogTitle}>Two-Factor Authentication</DialogTitle>
          </div>

          <div className={styles.dialogBody}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <p className={styles.description}>
                Please enter the two-factor authentication code sent to your device.
              </p>

              <div className={styles.inputGroup}>
                <input
                  autoComplete="one-time-code"
                  className={styles.input}
                  disabled={isLoading}
                  inputMode="numeric"
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setOtpCode(numericValue.slice(0, MAX_OTP_LENGTH));
                  }}
                  pattern="[0-9]*"
                  placeholder="000000"
                  ref={inputRef}
                  required
                  type="text"
                  value={otpCode}
                />
              </div>

              {errorMessage && (
                <p className={styles.errorMessage} role="alert">
                  {errorMessage}
                </p>
              )}

              <Button
                className={styles.submitButton}
                disabled={isLoading || otpCode.length !== MAX_OTP_LENGTH}
                isLoading={isLoading}
                type="submit"
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
};
